import React, { useEffect, useState } from "react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import { useUserContext } from "../../context";
import * as Yup from 'yup';
import { useApi } from "../../hooks/useApi";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardBody, CardHeader, Dialog, DialogBody, Typography } from "@material-tailwind/react";
import { ClockLoader } from "react-spinners";

export default function EditReceipt() {
    const { receiptNo } = useParams();
    const { fetchAccessToken, defaultHotel, assignments } = useUserContext();
    const [isLoading, setIsLoading] = useState(false);
    const { getReceiptByNo, saveReceipt } = useApi();
    const navigate = useNavigate();
    const [roomCategories, setRoomCategories] = useState([]);
    const [receipt, setReceipt] = useState(null);
    const [initialValues, setInitialValues] = useState(null);



    function toDateTimeLocalString(date) {
        const d = new Date(date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');

        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    }

    function toDateLocalString(date) {
        const d = new Date(date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');

        return `${yyyy}-${mm}-${dd}`;
    }

    function parseDDMMYYYYtoDate(dateStr) {
        const [datePart, timePart] = dateStr.split(" ");
        const [dd, MM, yyyy] = datePart.split("-").map(Number);
        const [hh = 0, mm = 0] = (timePart || "").split(":").map(Number);
        return new Date(yyyy, MM - 1, dd, hh, mm);
    }

    const now = new Date();
    const yesterday = new Date();
    yesterday.setHours(now.getHours() - 23);

    useEffect(() => {
        let getReceiptInternal = async (hotelId, receiptNo) => {
            setIsLoading(true);
            try {
                const token = await fetchAccessToken();
                const receipt = await getReceiptByNo(hotelId, receiptNo, token);
                if (receipt) {
                    setReceipt(receipt);
                    setInitialValues({
                        receiptNo: receipt.receiptNo,
                        receiptDate: toDateLocalString(parseDDMMYYYYtoDate(receipt.receiptDate)),
                        receiptDateEpoch: receipt.receiptDateEpoch,
                        totalAmount: receipt.totalAmount,
                        guestName: receipt.guestName,
                        guestMobile: receipt.guestMobile,
                        companyGST: receipt.companyGST,
                        companyDetails: receipt.companyDetails,
                        roomDetails: receipt.roomDetails.map(room => ({
                            roomNo: room.roomNo,
                            category: room.category
                        })),
                        checkInDateTime: toDateTimeLocalString(parseDDMMYYYYtoDate(receipt.checkInDateTime)),
                        checkOutDateTime: toDateTimeLocalString(parseDDMMYYYYtoDate(receipt.checkOutDateTime)),
                    });
                } else {
                    alert("Receipt doesn't exist in the hotel.");
                }
            } catch (ex) {
                alert("Something went wrong.");
            } finally {
                setIsLoading(false);
            }
        }
        if (defaultHotel && receiptNo) {
            getReceiptInternal(defaultHotel, receiptNo);
            let hotel = assignments?.hotels.find(hotel => hotel.id.toLowerCase() === defaultHotel.toLowerCase());
            setRoomCategories(hotel.roomTypes);
        }
    }, [defaultHotel, receiptNo]);

    function getTodayDateString() {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    const formik = useFormik({
        initialValues: initialValues || {
            receiptNo: "",
            receiptDate: getTodayDateString(),
            receiptDateEpoch: Date.now(),
            totalAmount: 0,
            guestName: "",
            guestMobile: "",
            companyGST: "",
            companyDetails: "",
            roomDetails: [],
            checkInDateTime: toDateTimeLocalString(yesterday),
            checkOutDateTime: toDateTimeLocalString(now),
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            guestMobile: Yup.string().required('Guest Mobile number is required'),
            guestName: Yup.string().required('Guest Name is required'),
            roomDetails: Yup.array().of(
                Yup.object().shape({
                    roomNo: Yup.string().required('Room number is required'),
                    category: Yup.string().required('Room category is required')
                })
            )
                .min(1, 'Room details cannot be empty'),
            totalAmount: Yup.number().min(0, 'Amount must be greater than or equal to 0'),
            receiptDate: Yup.date().required('Receipt date is required'),
            checkInDateTime: Yup.date().required('Check-in date is required'),
            checkOutDateTime: Yup.date().required('Checkout date is required'),
            totalDays: Yup.number().min(1, 'Total days must be greater than 0')
        }),
        onSubmit: async (values) => {
            if (!defaultHotel) {
                alert("Please select a hotel first.");
                return;
            }
            try {
                setIsLoading(true);
                const receiptData = {
                    hotelId: defaultHotel,
                    cardNo: 0,
                    receiptNo: receipt.receiptNo,
                    receiptDate: formatDateToDDMMYYYY(values.receiptDate),
                    receiptDateEpoch: values.receiptDateEpoch,
                    totalAmount: values.totalAmount,
                    guestName: values.guestName,
                    guestMobile: values.guestMobile,
                    companyGST: values.companyGST,
                    companyDetails: values.companyDetails,
                    roomDetails: [],
                    checkInDateTime: formatToCustomDateTime(values.checkInDateTime),
                    checkOutDateTime: formatToCustomDateTime(values.checkOutDateTime),
                    totalDays: values.totalDays,
                    createdOn: receipt.createdOn,
                    update: true,
                }
                receiptData.roomDetails = values.roomDetails.map(room => ({
                    roomNo: room.roomNo,
                    category: room.category,
                    totalDays: receiptData.totalDays,
                }));
                const token = await fetchAccessToken();
                await saveReceipt(defaultHotel, receiptData, token);
                alert("Receipt saved successfully!");
                setIsLoading(false);
                navigate("/admin/receipts");
            } catch (error) {
                setIsLoading(false);
                alert("Failed to save receipt. Please try again.");
            }
        },
        validateOnBlur: true,
        validateOnChange: false,
    });

    useEffect(() => {
        const checkInDateTime = formik.values.checkInDateTime;
        const checkOutDateTime = formik.values.checkOutDateTime;

        if (checkInDateTime && checkOutDateTime) {
            const totalDays = getDateDifference(checkInDateTime, checkOutDateTime);
            formik.setFieldValue("totalDays", totalDays);
        } else {
            formik.setFieldValue("totalDays", 0);
        }
    }, [formik.values.checkInDateTime, formik.values.checkOutDateTime]);

    function formatToCustomDateTime(dateString) {
        const date = new Date(dateString);

        const dd = String(date.getDate()).padStart(2, '0');
        const MM = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');

        return `${dd}-${MM}-${yyyy} ${hh}:${mm}`;
    }

    function formatDateToDDMMYYYY(dateString) {
        const date = new Date(dateString);
        const dd = String(date.getDate()).padStart(2, '0');
        const MM = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const yyyy = date.getFullYear();
        return `${dd}-${MM}-${yyyy}`;
    }

    function getDateDifference(dateString1, dateString2) {
        const date1 = new Date(dateString1);
        const date2 = new Date(dateString2);

        if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
            throw new Error("Invalid date format. Expected YYYY-MM-DDTHH:mm");
        }

        const differenceMs = Math.abs(date2 - date1);
        const differenceDays = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

        return differenceDays === 0 ? 1 : differenceDays;
    }

    const handleCustomSubmit = async (e) => {
        e.preventDefault();
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            const errorMessages = Object.values(errors).flatMap((v) => {
                if (Array.isArray(v)) return v;
                if (typeof v === 'object') return Object.values(v);
                return v;
            });
            alert("Validation Errors:\n" + errorMessages.join("\n"));
        } else {
            formik.handleSubmit();
        }
    };

    return (
        <div>
            <Card className="max-w-2xl mx-auto bg-white shadow rounded">
                <CardHeader variant="gradient" color="gray" className="mt-6 mb-8 p-6 flex flex-row justify-between">
                    <Typography variant="h6" color="white">
                        Edit Receipt
                    </Typography>

                </CardHeader>
                <CardBody className="overflow-scroll px-0 pt-0 pb-2 p-6">
                    <form onSubmit={handleCustomSubmit} className="space-y-4">
                        <div>
                            <label className="block font-medium">Receipt No <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="receiptNo"
                                value={formik.values.receiptNo} disabled={true}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Receipt Date <span className="text-red-500">*</span></label>
                            <input
                                type="date"
                                name="receiptDate"
                                onChange={e => {
                                    formik.handleChange(e);
                                    const selectedDate = e.target.value;
                                    if (selectedDate) {
                                        const now = new Date();
                                        const hours = now.getHours();
                                        const minutes = now.getMinutes();
                                        const seconds = now.getSeconds();
                                        const milliseconds = now.getMilliseconds();
                                        const fullDate = new Date(selectedDate);
                                        fullDate.setHours(hours, minutes, seconds, milliseconds);
                                        formik.setFieldValue("receiptDateEpoch", fullDate.getTime());
                                    }
                                }}
                                value={formik.values.receiptDate}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Total Amount <span className="text-red-500">*</span></label>
                            <input
                                type="number"
                                name="totalAmount"
                                onChange={formik.handleChange}
                                value={formik.values.totalAmount}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Guest Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="guestName"
                                onChange={formik.handleChange}
                                value={formik.values.guestName}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Guest Mobile <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="guestMobile"
                                onChange={formik.handleChange}
                                value={formik.values.guestMobile}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Company GST</label>
                            <input
                                type="text"
                                name="companyGST"
                                onChange={formik.handleChange}
                                value={formik.values.companyGST}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Company Details</label>
                            <textarea
                                name="companyDetails"
                                onChange={formik.handleChange}
                                value={formik.values.companyDetails}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <FormikProvider value={formik}>
                            <FieldArray
                                name="roomDetails"
                                render={arrayHelpers => (
                                    <div>
                                        <label className="block font-medium mb-2">Room Details <span className="text-red-500">*</span></label>
                                        {formik.values.roomDetails.map((room, idx) => (
                                            <div key={idx} className="mb-4 border p-3 rounded bg-gray-50">
                                                <div className="flex flex-col sm:flex-row gap-2 mb-2">
                                                    <input
                                                        type="text"
                                                        name={`roomDetails[${idx}].roomNo`}
                                                        placeholder="Room No"
                                                        onChange={formik.handleChange}
                                                        value={room.roomNo}
                                                        className="border rounded px-2 py-1 flex-1"
                                                    />
                                                    <select label="Category" color="blue" className="bg-white text-gray-900 border border-gray-300 rounded-md py-2 pl-3 pr-10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                                        name={`roomDetails[${idx}].category`} id={`roomDetails[${idx}].category`}
                                                        onChange={formik.handleChange}
                                                        value={room.category}>
                                                        <option value="">Select</option>
                                                        {roomCategories.map((category, i) => (
                                                            <option key={i} value={category.category}>{category.category}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        onClick={() => arrayHelpers.remove(idx)}
                                                        className="text-red-500 px-2"
                                                        disabled={formik.values.roomDetails.length === 1}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                arrayHelpers.push({ roomNo: "", category: "" })
                                            }
                                            className="bg-blue-500 text-white px-3 py-1 rounded"
                                        >
                                            Add Room
                                        </button>
                                    </div>
                                )}
                            />
                        </FormikProvider>
                        <div>
                            <label className="block font-medium">Check-In DateTime <span className="text-red-500">*</span></label>
                            <input
                                type="datetime-local"
                                name="checkInDateTime"
                                onChange={formik.handleChange}
                                value={formik.values.checkInDateTime}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Check-Out DateTime <span className="text-red-500">*</span></label>
                            <input
                                type="datetime-local"
                                name="checkOutDateTime"
                                onChange={formik.handleChange}
                                value={formik.values.checkOutDateTime}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Total Days</label>
                            <input
                                type="number"
                                name="totalDays"
                                onChange={formik.handleChange}
                                value={formik.values.totalDays} disabled={true}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-green-600 text-white py-2 rounded font-semibold"
                        >
                            Save
                        </button>
                    </form>
                </CardBody>
            </Card>
            <Dialog open={isLoading} className="bg-transparent shadow-none" size="md">
                <DialogBody className="bg-transparent shadow-none flex justify-center">
                    <ClockLoader color="#2be60d" />
                </DialogBody>
            </Dialog>
        </div>
    );
}